import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { submitFormData } from '../../../pages/api';

const Form_17 = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  // Initialize formData with ivParameters as an empty array
  const savedData = sessionStorage.getItem('RFIDReadabilityF17formData');
  const parsedSavedData = savedData ? JSON.parse(savedData) : null;
  const [formData, setFormData] = useState(
    parsedSavedData
      ? {
          ...parsedSavedData,
          modules: Object.keys(parsedSavedData.modules).reduce((acc, key) => ({
            ...acc,
            [key]: {
              ...parsedSavedData.modules[key],
              ivParameters: Array.isArray(parsedSavedData.modules[key].ivParameters)
                ? parsedSavedData.modules[key].ivParameters
                : [],
            },
          }), {}),
        }
      : {
          date: sessionStorage.getItem('inspectionDate') || '',
          modules: {
            module1: { line: '', module: '', ivParameters: [] },
            module2: { line: '', module: '', ivParameters: [] },
            module3: { line: '', module: '', ivParameters: [] },
            module4: { line: '', module: '', ivParameters: [] },
            module5: { line: '', module: '', ivParameters: [] },
          },
        }
  );
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    // Prefill from inspectionData
    const inspectionData = sessionStorage.getItem('inspectionData');
    if (inspectionData) {
      try {
        const parsedInspectionData = JSON.parse(inspectionData);
        if (parsedInspectionData.RFIDReadability) {
          setFormData((prevData) => ({
            ...prevData,
            date: parsedInspectionData.RFIDReadability.date || prevData.date,
            modules: Object.keys(prevData.modules).reduce((acc, key) => ({
              ...acc,
              [key]: {
                ...prevData.modules[key],
                ...parsedInspectionData.RFIDReadability.modules[key],
                ivParameters: Array.isArray(parsedInspectionData.RFIDReadability.modules[key]?.ivParameters)
                  ? parsedInspectionData.RFIDReadability.modules[key].ivParameters
                  : [],
              },
            }), {}),
          }));
        }
      } catch (error) {
        console.error('Error parsing inspectionData:', error);
      }
    }

    // Fetch uploaded files
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchUploadedFiles = async () => {
      const projectId = localStorage.getItem('selectedProject');
      const inspectionDate = sessionStorage.getItem('inspectionDate');
      const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
      const inspectionShift = sessionStorage.getItem('inspectionShift');
      if (!projectId || !inspectionDate || !inspectionSupplier || !inspectionShift) return;

      const moduleKeys = ['module1', 'module2', 'module3', 'module4', 'module5'];
      const fetchedFiles = {};
      await Promise.all(
        moduleKeys.map(async (key) => {
          try {
            const response = await axios.get(`${baseURL}get_uploaded_inspection_files`, {
              params: {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                formName: 'RFIDReadability',
                inputFieldId: `${key}file`,
              },
            });
            fetchedFiles[key] = response.data.files || [];
          } catch (error) {
            console.error(`Error fetching files for ${key}:`, error);
            fetchedFiles[key] = [];
          }
        })
      );
      setUploadedFiles(fetchedFiles);
    };

    fetchUploadedFiles();
  }, []);

  // Save sanitized formData to sessionStorage (excluding base64 data)
  useEffect(() => {
    const sanitizedFormData = {
      ...formData,
      modules: Object.keys(formData.modules).reduce((acc, key) => ({
        ...acc,
        [key]: {
          ...formData.modules[key],
          ivParameters: formData.modules[key].ivParameters.map((fileData) => ({
            filename: fileData.filename, // Save only filename, exclude base64
          })),
        },
      }), {}),
    };
    sessionStorage.setItem('RFIDReadabilityF17formData', JSON.stringify(sanitizedFormData));
  }, [formData]);

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
    });

  const handleInputChange = (e, moduleKey, field) => {
    const { value } = e.target;
    setFormData((prev) => {
      const updatedModules = field
        ? { ...prev.modules, [moduleKey]: { ...prev.modules[moduleKey], [field]: value } }
        : { ...prev.modules, [moduleKey]: { ...prev.modules[moduleKey], line: value } };
      return { ...prev, modules: updatedModules };
    });
    setErrors((prev) => ({ ...prev, [`${moduleKey}_${field || 'line'}`]: '' }));
  };

  const handleFileChange = async (moduleKey, e) => {
    const files = Array.from(e.target.files);
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];

    // Validate file types
    const invalidFiles = files.filter((file) => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setErrors((prev) => ({
        ...prev,
        [`${moduleKey}_ivParameters`]: 'Only PDF, JPEG, or PNG files are allowed.',
      }));
      return;
    }

    try {
      // Convert all files to base64
      const fileData = await Promise.all(
        files.map(async (file) => ({
          base64: await convertToBase64(file),
          filename: file.name,
        }))
      );

      setFormData((prev) => ({
        ...prev,
        modules: {
          ...prev.modules,
          [moduleKey]: {
            ...prev.modules[moduleKey],
            ivParameters: fileData,
          },
        },
      }));
      setErrors((prev) => ({ ...prev, [`${moduleKey}_ivParameters`]: '' }));
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [`${moduleKey}_ivParameters`]: 'Error processing files.',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const projectId = localStorage.getItem('selectedProject');
      const inspectionDate = sessionStorage.getItem('inspectionDate');
      const inspectionShift = sessionStorage.getItem('inspectionShift');
      const inspectorName = sessionStorage.getItem('username');
      const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

      // Upload files first
      const moduleKeys = Object.keys(formData.modules);
      for (const key of moduleKeys) {
        const module = formData.modules[key];
        if (Array.isArray(module.ivParameters) && module.ivParameters.length > 0) {
          const filesToUpload = module.ivParameters.map((fileData) => ({
            inputFieldId: `${key}file`,
            file: fileData.base64,
            filename: fileData.filename,
          }));

          await axios.post(`${baseURL}upload_inspection_files`, {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            formName: 'RFIDReadability',
            files: filesToUpload,
          });

          // Fetch updated files
          const response = await axios.get(`${baseURL}get_uploaded_inspection_files`, {
            params: {
              projectId,
              inspectionSupplier,
              inspectionDate,
              inspectionShift,
              formName: 'RFIDReadability',
              inputFieldId: `${key}file`,
            },
          });
          setUploadedFiles((prev) => ({
            ...prev,
            [key]: response.data.files || [],
          }));
        }
      }

      // Prepare formData for submission, excluding base64 data
      const sanitizedFormData = {
        ...formData,
        modules: Object.keys(formData.modules).reduce((acc, key) => ({
          ...acc,
          [key]: {
            ...formData.modules[key],
            ivParameters: formData.modules[key].ivParameters.map((fileData) => ({
              filename: fileData.filename, // Include only filename
            })),
          },
        }), {}),
      };

      // Log the payload for debugging

      await submitFormData({
        projectId,
        inspectionSupplier,
        inspectionDate,
        inspectionShift,
        inspectorName,
        status: 'completed',
        RFIDReadability: sanitizedFormData,
      });

      alert('Form submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error.response?.data || error.message);
      alert(`Failed to submit form: ${error.response?.data?.error || 'Unknown error'}`);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg space-y-4">
      <h2 className="text-xl font-bold text-center">RFID Readability and Data Verification</h2>
      <p className="text-center font-semibold mb-4">Frequency: 5 Modules Per Shift</p>
      <div className="w-72">
        <label className="block font-semibold mb-1">Date</label>
        <input
          type="date"
          value={formData.date}
          readOnly
          className="w-full p-2 border rounded"
        />
      </div>
      {Object.keys(formData.modules).map((moduleKey, index) => (
        <div key={moduleKey} className="mt-6 border-2 border-gray-300 p-4 rounded-lg">
          <h3 className="text-lg font-semibold">{`Module ${index + 1}`}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1">Line</label>
              <input
                type="text"
                value={formData.modules[moduleKey].line}
                onChange={(e) => handleInputChange(e, moduleKey)}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Module</label>
              <input
                type="text"
                value={formData.modules[moduleKey].module}
                onChange={(e) => handleInputChange(e, moduleKey, 'module')}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block font-semibold mb-1">IV Parameters (Upload Photos)</label>
            <input
              type="file"
              multiple
              onChange={(e) => handleFileChange(moduleKey, e)}
              className="w-full p-2 border rounded"
            />
            {errors[`${moduleKey}_ivParameters`] && (
              <p className="text-red-500 text-xs">{errors[`${moduleKey}_ivParameters`]}</p>
            )}
            {Array.isArray(formData.modules[moduleKey].ivParameters) && formData.modules[moduleKey].ivParameters.length > 0 && (
              <div className="text-sm text-gray-500 mt-1">
                <p>Uploaded Files:</p>
                <ul>
                  {formData.modules[moduleKey].ivParameters.map((fileData, idx) => (
                    <li key={idx}>{fileData.filename}</li>
                  ))}
                </ul>
              </div>
            )}
            {uploadedFiles[moduleKey]?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-semibold">Previously Uploaded:</p>
                <ul className="text-xs text-gray-600">
                  {uploadedFiles[moduleKey].map((file, index) => (
                    <li key={index}>
                      {file.url && file.url.trim() ? (
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {file.filename}
                        </a>
                      ) : (
                        <span>{file.filename} (No URL)</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ))}
      <div className="flex justify-between mt-4">
        <button
          type="button"
          onClick={() => navigate('/inspections/section2/form16')}
          className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
        >
          Previous
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-[#424752] text-white rounded hover:bg-gray-500"
        >
          Submit
        </button>
        <button
          type="button"
          onClick={() => navigate('/inspections/section2/form18')}
          className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Form_17;